import { Button } from "@/components/ui/button";
import { Calendar, Clock, ArrowLeft, User, Tag } from "lucide-react";
import { useLocation } from "wouter";
import { Helmet } from "react-helmet-async";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import type { BlogPost } from "@shared/blog-schema";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BLOG_FAQS } from "@/data/blog-faqs";

const PROD_BASE = "https://saasgama.com";

function getCanonicalUrl(path: string): string {
  if (typeof window === "undefined") return `${PROD_BASE}${path}`;
  const { protocol, host } = window.location;
  if (host === "saasgama.com" || host === "www.saasgama.com") {
    return `${PROD_BASE}${path}`;
  }
  return `${protocol}//${host}${path}`;
}

function sanitizeBlogContent(html: string): string {
  return html
    .replace(/<h1([^>]*)>/gi, '<h2$1>')
    .replace(/<\/h1>/gi, '</h2>')
    .replace(/<p><br\s*\/?><\/p>/gi, '')
    .trim();
}

export default function BlogPostPage() {
  const [location, navigate] = useLocation();

  const slug = location.split('/blog/')[1];
  const canonicalUrl = getCanonicalUrl(`/blog/${slug || ""}`);
  const faqs = slug ? BLOG_FAQS[slug] : undefined;
  const { data: post, isLoading } = useQuery<BlogPost>({
    queryKey: ['/api/blog/posts', slug],
    queryFn: async () => {
      const res = await fetch(`/api/blog/posts/${slug}`);
      if (!res.ok) throw new Error('Not found');
      return res.json();
    },
    enabled: !!slug,
    retry: false,
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-gray-50 border-b">
          <div className="container mx-auto px-4 py-4">
            <Button variant="ghost" onClick={() => navigate('/blog')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Button>
          </div>
        </div>
        <div className="py-16">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Blog Post Not Found</h1>
            <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist or has been moved.</p>
            <Button onClick={() => navigate('/blog')} className="bg-blue-600 hover:bg-blue-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription || ''} />
        {post.tags && post.tags.length > 0 && <meta name="keywords" content={post.tags.join(", ")} />}
        {/* Canonical: self-referential on dev, production URL on prod */}
        <link rel="canonical" href={canonicalUrl} />
        {/* Alternate: www variant — only on production */}
        {canonicalUrl.startsWith(PROD_BASE) && (
          <link rel="alternate" href={`https://www.saasgama.com/blog/${post.slug}`} hrefLang="en" />
        )}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BlogPosting",
              "@id": `https://saasgama.com/blog/${post.slug}/#article`,
              "headline": post.title,
              "description": post.excerpt,
              "image": ["https://saasgama.com/blog-image.jpg"],
              "author": { "@type": "Organization", "name": "SaaSGama" },
              "publisher": { "@type": "Organization", "name": "SaaSGama", "logo": { "@type": "ImageObject", "url": "https://saasgama.com/logo.png" } },
              "datePublished": post.publishedAt,
              "dateModified": post.updatedAt,
              "mainEntityOfPage": { "@type": "WebPage", "@id": `https://saasgama.com/blog/${post.slug}` },
              "articleSection": "SaaS Management",
              "keywords": post.tags || []
            },
            { "@type": "BreadcrumbList", "@id": `https://saasgama.com/blog/${post.slug}/#breadcrumb`, "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saasgama.com" },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://saasgama.com/blog" },
              { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://saasgama.com/blog/${post.slug}` }
            ]},
            ...(faqs ? [{
              "@type": "FAQPage",
              "@id": `https://saasgama.com/blog/${post.slug}/#faq`,
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.q,
                "acceptedAnswer": { "@type": "Answer", "text": faq.a }
              }))
            }] : [])
          ]
        })}</script>
      </Helmet>

      {/* Back Button */}
      <div className="bg-gray-50 border-b">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/blog')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>
        </div>
      </div>

      {/* Blog Post Content */}
      <article className="py-8 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Article Header */}
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-6">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                {post.authorName}
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {formatDate((post.publishedAt ?? post.createdAt).toString())}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                5 min read
              </div>
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {post.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center">
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

          </header>

          {/* Article Content */}
          <div
            className="prose prose-lg max-w-none text-gray-800 leading-relaxed
              prose-h2:text-2xl prose-h2:font-bold prose-h2:text-gray-900 prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:font-semibold prose-h3:text-gray-800 prose-h3:mt-6 prose-h3:mb-3
              prose-p:mb-4 prose-ul:mb-4 prose-ol:mb-4 prose-li:mb-1"
            dangerouslySetInnerHTML={{ __html: sanitizeBlogContent(post.content) }}
          />

          {/* FAQ Section */}
          {faqs && (
            <div className="mt-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="w-full space-y-3">
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border border-gray-200 rounded-lg px-5 bg-white">
                    <AccordionTrigger className="text-left font-semibold text-gray-800 hover:text-blue-600">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pt-1">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* Author Info */}
          <div className="mt-12 p-6 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                {post.authorName.charAt(0)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{post.authorName}</h3>
                <p className="text-gray-600">{post.authorEmail === "team@saasgama.com" ? "support@saasgama.com" : post.authorEmail}</p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Ready to optimize your SaaS spending?
              </h3>
              <p className="text-gray-600 mb-6">
                Join thousands of organizations using SaaSGama to cut costs and streamline their software portfolio.
              </p>
              <Button onClick={() => navigate('/register')} className="bg-blue-600 hover:bg-blue-700">
                Get Your Trial Today
              </Button>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
